import React, { useEffect, useMemo, useState } from 'react';
import { PiDownloadSimple, PiPackageLight } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import Loading from 'src/modules/shared/components/atoms/loading';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { useAlert } from 'src/contexts/alert-context';
import {
  WebsiteTemplateRegistryVisibility,
  type WebsiteTemplateRegistryRecord,
  type WebsiteTemplateRegistryRevision,
  type WebsiteTemplateRegistryRevisionsResponse,
  type WebsiteTemplateRegistryValidationIssue,
  type WebsiteTemplateRegistryValidationReport,
} from 'src/api/generated';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { WebsiteTemplateRegistryPermission } from 'src/modules/shared/types/permissions.type';
import {
  useCreateWebsiteTemplateRegistryUploadSession,
  useFinalizeWebsiteTemplateRegistryUpload,
  useUpdateWebsiteTemplateRegistryEntry,
  useWebsiteTemplateRegistry,
  useWebsiteTemplateRegistryRevisions,
  useWebsiteTemplateRegistryValidationReport,
} from '../../hooks/api/websites';

interface TemplateMetadataDraft {
  name: string;
  description: string;
  author: string;
  visibility: WebsiteTemplateRegistryVisibility;
}

const STATUS_LABELS: Record<string, string> = {
  uploading: 'Subiendo',
  validating: 'Validando',
  ready: 'Lista',
  failed: 'Fallida',
};

const TEMPLATE_REQUIREMENTS_DOC_URL =
  '/docs/website-template-registry-requirements.md';

const PRIVATE_VISIBILITY_OPTION = {
  value: WebsiteTemplateRegistryVisibility.PRIVATE,
  content: 'Privado para mi tenant',
};

const PUBLIC_VISIBILITY_OPTION = {
  value: WebsiteTemplateRegistryVisibility.PUBLIC,
  content: 'Publico para todos los tenants',
};

/**
 * Maps a visibility select value to the registry visibility.
 *
 * @param value - Raw value from a visibility `<select>`.
 * @returns `PUBLIC` for `public`, otherwise `PRIVATE`.
 */
const toRegistryVisibility = (
  value: string
): WebsiteTemplateRegistryVisibility =>
  value === String(WebsiteTemplateRegistryVisibility.PUBLIC)
    ? WebsiteTemplateRegistryVisibility.PUBLIC
    : WebsiteTemplateRegistryVisibility.PRIVATE;

const formatRegistryStatus = (status: string): string =>
  STATUS_LABELS[status] ?? status;

const formatDateTime = (value?: string): string =>
  value ? new Date(value).toLocaleString('es-CL') : 'Sin timestamp';

const getUploadContentType = (file: File): string =>
  file.type.trim().length > 0 ? file.type : 'application/zip';

const getUploadErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Reintenta en unos segundos.';

const buildRegistryAlertMessage = (title: string, subtitle?: string): string =>
  subtitle ? `${title}. ${subtitle}` : title;

const validateSelectedPackageFile = (file: File | null): string | null => {
  if (!file) {
    return 'Debes elegir un archivo .zip para iniciar la carga.';
  }

  return file.name.toLowerCase().endsWith('.zip')
    ? null
    : 'El registry solo acepta paquetes .zip.';
};

const uploadPackage = async (
  file: File,
  uploadUrl: string,
  uploadHeaders: Record<string, string>
): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: uploadHeaders,
    body: file,
  });

  if (!response.ok) {
    throw new Error('No pudimos subir el paquete al storage temporal.');
  }
};

/** Props for the upload package section. */
interface UploadPackageSectionProps {
  /** Whether the current user can create registry packages. */
  canCreateUploads: boolean;
  /** Currently selected local package file. */
  selectedFile: File | null;
  /** Whether upload-session creation is running. */
  creatingUploadSession: boolean;
  /** Whether upload finalization is running. */
  finalizingUpload: boolean;
  /** Whether the current user can publish templates for every tenant. */
  canPublish: boolean;
  /** Visibility of the entry that receives the uploaded revision. */
  visibility: WebsiteTemplateRegistryVisibility;
  /** Called when the file input changes. */
  onFileChange: (file: File | null) => void;
  /** Called when the user changes the upload visibility. */
  onVisibilityChange: (visibility: WebsiteTemplateRegistryVisibility) => void;
  /** Called when the user starts the upload flow. */
  onUpload: () => void;
}

const UploadPackageSection: React.FC<UploadPackageSectionProps> = ({
  canCreateUploads,
  selectedFile,
  creatingUploadSession,
  finalizingUpload,
  canPublish,
  visibility,
  onFileChange,
  onVisibilityChange,
  onUpload,
}) => {
  const isProcessing = creatingUploadSession || finalizingUpload;
  const isUploadDisabled = !canCreateUploads || !selectedFile || isProcessing;

  return (
    <section className="space-y-4 rounded-xl border border-slate-200 p-4">
      <div>
        <Text weight="medium">Subir paquete</Text>
        <Text size="tiny" color="secondary" className="mt-1">
          El paquete debe incluir <code>template.json</code>,{' '}
          <code>website-package.json</code> y un <code>dist/</code> completo.
        </Text>
      </div>

      <label className="block rounded-xl border border-dashed border-slate-300 p-4">
        <Text size="tiny" color="secondary">
          Archivo .zip
        </Text>
        <input
          type="file"
          accept=".zip,application/zip"
          className="mt-2 block w-full text-sm"
          onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
          disabled={!canCreateUploads}
        />
      </label>

      {canPublish ? (
        <div className="space-y-2">
          <LabelSelect
            label="Visibilidad del paquete"
            labelPosition="top"
            options={[PRIVATE_VISIBILITY_OPTION, PUBLIC_VISIBILITY_OPTION]}
            selectProps={{
              'aria-label': 'Visibilidad del paquete',
              value: visibility,
              disabled: !canCreateUploads || isProcessing,
              onChange: (event: React.ChangeEvent<HTMLSelectElement>) =>
                onVisibilityChange(toRegistryVisibility(event.target.value)),
            }}
          />
          <Text size="tiny" color="secondary">
            Para subir una nueva revision de un template publico elige Publico:
            se agrega a la entrada publica con la misma clave y version.
          </Text>
        </div>
      ) : null}

      {selectedFile ? (
        <div className="rounded-xl bg-slate-50 p-4">
          <Text size="tiny" weight="medium">
            {selectedFile.name}
          </Text>
          <Text size="tiny" color="secondary">
            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
          </Text>
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button onClick={onUpload} disabled={isUploadDisabled}>
          {isProcessing ? 'Procesando...' : 'Subir paquete'}
        </Button>
      </div>
    </section>
  );
};

/** Props for a registry entry selection button. */
interface RegistryEntryButtonProps {
  /** Registry entry displayed by the button. */
  item: WebsiteTemplateRegistryRecord;
  /** Whether this entry is currently selected. */
  selected: boolean;
  /** Called with the selected registry template id. */
  onSelect: (registryTemplateId: string) => void;
}

const RegistryEntryButton: React.FC<RegistryEntryButtonProps> = ({
  item,
  selected,
  onSelect,
}) => (
  <button
    type="button"
    className={`w-full rounded-xl border p-4 text-left transition ${
      selected
        ? 'border-sky-300 bg-sky-50'
        : 'border-slate-200 hover:border-slate-300'
    }`}
    onClick={() => onSelect(item.registryTemplateId)}
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <Text weight="medium">
          {item.templateSummary?.name ?? item.templateKey}
        </Text>
        <Text size="tiny" color="secondary">
          {item.templateKey}@{item.templateVersion} ·{' '}
          {item.visibility === WebsiteTemplateRegistryVisibility.PRIVATE
            ? 'Privada'
            : 'Publica'}{' '}
          ·{' '}
          {item.latestRevision
            ? `Ultima revision: ${formatRegistryStatus(item.latestRevision.status)}`
            : 'Sin revisiones'}
        </Text>
      </div>
      {item.activeRevisionId ? (
        <Text size="tiny" color="secondary">
          Activa: {item.activeRevisionId.slice(0, 8)}
        </Text>
      ) : null}
    </div>
  </button>
);

/** Props for the registry entries section. */
interface RegistryEntriesSectionProps {
  /** Registry entries available to manage. */
  items: WebsiteTemplateRegistryRecord[];
  /** Whether the registry list is loading. */
  loading: boolean;
  /** Registry list error, when loading failed. */
  error: unknown;
  /** Currently selected registry template id. */
  selectedRegistryTemplateId?: string;
  /** Called when the user selects a registry entry. */
  onSelect: (registryTemplateId: string) => void;
}

const RegistryEntriesSection: React.FC<RegistryEntriesSectionProps> = ({
  items,
  loading,
  error,
  selectedRegistryTemplateId,
  onSelect,
}) => (
  <section className="space-y-4 rounded-xl border border-slate-200 p-4">
    <div>
      <Text weight="medium">Entradas del registry</Text>
      <Text size="tiny" color="secondary" className="mt-1">
        Solo las revisiones en estado <code>Lista</code> quedan disponibles para
        seleccion en sitios.
      </Text>
    </div>

    {loading ? <Loading /> : null}
    {!loading && error ? (
      <ErrorState
        title="No pudimos cargar el registry"
        description="Reintenta en unos segundos."
      />
    ) : null}

    {!loading && !error && items.length === 0 ? (
      <Text size="tiny" color="secondary">
        Todavia no hay paquetes cargados.
      </Text>
    ) : null}

    <div className="space-y-3">
      {items.map((item) => (
        <RegistryEntryButton
          key={item.registryTemplateId}
          item={item}
          selected={item.registryTemplateId === selectedRegistryTemplateId}
          onSelect={onSelect}
        />
      ))}
    </div>
  </section>
);

interface TemplateMetadataSectionProps {
  selectedRegistryEntry: WebsiteTemplateRegistryRecord | null;
  draft: TemplateMetadataDraft;
  canUpdate: boolean;
  canPublish: boolean;
  saving: boolean;
  onDraftChange: (draft: TemplateMetadataDraft) => void;
  onSave: () => void;
}

const TemplateMetadataSection: React.FC<TemplateMetadataSectionProps> = ({
  selectedRegistryEntry,
  draft,
  canUpdate,
  canPublish,
  saving,
  onDraftChange,
  onSave,
}) => {
  const canDisplayPublicOption =
    canPublish ||
    selectedRegistryEntry?.visibility ===
      WebsiteTemplateRegistryVisibility.PUBLIC;
  const visibilityOptions = [
    PRIVATE_VISIBILITY_OPTION,
    ...(canDisplayPublicOption ? [PUBLIC_VISIBILITY_OPTION] : []),
  ];

  return (
    <section className="space-y-4 rounded-xl border border-slate-200 p-4">
      <div>
        <Text weight="medium">Datos visibles del template</Text>
        <Text size="tiny" color="secondary" className="mt-1">
          Cambia el nombre, descripcion, autor y visibilidad sin subir una nueva
          revision.
        </Text>
      </div>

      {!selectedRegistryEntry ? (
        <Text size="tiny" color="secondary">
          Selecciona una entrada para editar sus datos visibles.
        </Text>
      ) : (
        <>
          <LabelTextInput
            label="Nombre"
            labelPosition="top"
            inputProps={{
              value: draft.name,
              disabled: !canUpdate,
              onChange: (event) =>
                onDraftChange({ ...draft, name: event.target.value }),
            }}
          />

          <LabelTextArea
            label="Descripcion"
            labelPosition="top"
            inputProps={{
              className: 'min-h-[88px]',
              value: draft.description,
              disabled: !canUpdate,
              onChange: (event) =>
                onDraftChange({ ...draft, description: event.target.value }),
            }}
          />

          <LabelTextInput
            label="Autor"
            labelPosition="top"
            inputProps={{
              value: draft.author,
              disabled: !canUpdate,
              onChange: (event) =>
                onDraftChange({ ...draft, author: event.target.value }),
            }}
          />

          <LabelSelect
            label="Visibilidad"
            labelPosition="top"
            options={visibilityOptions}
            selectProps={{
              value: draft.visibility,
              disabled: !canUpdate,
              onChange: (event: React.ChangeEvent<HTMLSelectElement>) =>
                onDraftChange({
                  ...draft,
                  visibility: toRegistryVisibility(event.target.value),
                }),
            }}
          />

          <div className="flex justify-end">
            <Button
              onClick={onSave}
              disabled={!canUpdate || saving || !draft.name.trim()}
              loading={saving}
            >
              Guardar datos
            </Button>
          </div>
        </>
      )}
    </section>
  );
};

/** Props for a revision selection button. */
interface RevisionButtonProps {
  /** Revision displayed by the button. */
  revision: WebsiteTemplateRegistryRevision;
  /** Whether this revision is currently selected. */
  selected: boolean;
  /** Called with the selected revision id. */
  onSelect: (revisionId: string) => void;
}

const RevisionButton: React.FC<RevisionButtonProps> = ({
  revision,
  selected,
  onSelect,
}) => (
  <button
    type="button"
    className={`w-full rounded-xl border p-4 text-left transition ${
      selected
        ? 'border-sky-300 bg-sky-50'
        : 'border-slate-200 hover:border-slate-300'
    }`}
    onClick={() => onSelect(revision.revisionId)}
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <Text weight="medium">{revision.revisionId}</Text>
        <Text size="tiny" color="secondary">
          Estado: {formatRegistryStatus(revision.status)}
        </Text>
      </div>
      <Text size="tiny" color="secondary">
        {formatDateTime(revision.createdAt)}
      </Text>
    </div>
  </button>
);

/** Props for the revisions panel. */
interface RevisionsPanelProps {
  /** Selected registry entry, when one is selected. */
  selectedRegistryEntry: WebsiteTemplateRegistryRecord | null;
  /** Currently selected registry template id. */
  selectedRegistryTemplateId?: string;
  /** Revisions response for the selected registry entry. */
  revisions: WebsiteTemplateRegistryRevisionsResponse | null;
  /** Whether revisions are loading. */
  loading: boolean;
  /** Revisions error, when loading failed. */
  error: unknown;
  /** Currently selected revision id. */
  selectedRevisionId?: string;
  /** Called when the user selects a revision. */
  onSelectRevision: (revisionId: string) => void;
}

const RevisionsPanel: React.FC<RevisionsPanelProps> = ({
  selectedRegistryEntry,
  selectedRegistryTemplateId,
  revisions,
  loading,
  error,
  selectedRevisionId,
  onSelectRevision,
}) => (
  <Panel>
    <PanelHeader className="px-4 py-3 border-b">
      <div>
        <Text weight="medium">Revisiones</Text>
        <Text size="tiny" color="secondary" className="mt-1">
          {selectedRegistryEntry
            ? `${selectedRegistryEntry.templateKey}@${selectedRegistryEntry.templateVersion}`
            : 'Selecciona una entrada para revisar sus versiones.'}
        </Text>
      </div>
    </PanelHeader>

    {selectedRegistryTemplateId ? (
      <RevisionList
        revisions={revisions}
        loading={loading}
        error={error}
        selectedRevisionId={selectedRevisionId}
        onSelectRevision={onSelectRevision}
      />
    ) : (
      <Text size="tiny" color="secondary">
        Selecciona una entrada para ver sus revisiones.
      </Text>
    )}
  </Panel>
);

/** Props for the selected entry revision list. */
interface RevisionListProps {
  /** Revisions response for the selected registry entry. */
  revisions: WebsiteTemplateRegistryRevisionsResponse | null;
  /** Whether revisions are loading. */
  loading: boolean;
  /** Revisions error, when loading failed. */
  error: unknown;
  /** Currently selected revision id. */
  selectedRevisionId?: string;
  /** Called when the user selects a revision. */
  onSelectRevision: (revisionId: string) => void;
}

const RevisionList: React.FC<RevisionListProps> = ({
  revisions,
  loading,
  error,
  selectedRevisionId,
  onSelectRevision,
}) => (
  <>
    {loading ? <Loading /> : null}
    {!loading && error ? (
      <ErrorState
        title="No pudimos cargar las revisiones"
        description="Reintenta en unos segundos."
      />
    ) : null}

    {!loading && !error ? (
      <div className="space-y-3">
        {(revisions?.revisions ?? []).map((revision) => (
          <RevisionButton
            key={revision.revisionId}
            revision={revision}
            selected={revision.revisionId === selectedRevisionId}
            onSelect={onSelectRevision}
          />
        ))}
      </div>
    ) : null}
  </>
);

/** Props for a validation issue card. */
interface ValidationIssueCardProps {
  /** Validation issue to render. */
  issue: WebsiteTemplateRegistryValidationIssue;
  /** Index used only to keep duplicate issue codes renderable. */
  index: number;
  /** Color variant for the validation issue. */
  variant: 'error' | 'warning';
}

const ValidationIssueCard: React.FC<ValidationIssueCardProps> = ({
  issue,
  index,
  variant,
}) => {
  const classes =
    variant === 'error'
      ? 'rounded-xl border border-rose-200 bg-rose-50 p-3'
      : 'rounded-xl border border-amber-200 bg-amber-50 p-3';

  return (
    <div key={`${issue.code}-${index}`} className={classes}>
      <Text size="tiny" weight="medium">
        {issue.code}
      </Text>
      <Text size="tiny" color="secondary" className="mt-1">
        {issue.message}
      </Text>
      {issue.path ? (
        <Text size="tiny" color="secondary" className="mt-1">
          {issue.path}
        </Text>
      ) : null}
    </div>
  );
};

/** Props for a validation issue section. */
interface ValidationIssueSectionProps {
  /** Section title. */
  title: string;
  /** Issues to render in this section. */
  issues: WebsiteTemplateRegistryValidationIssue[];
  /** Empty state text when there are no issues. */
  emptyText: string;
  /** Color variant for issue cards. */
  variant: 'error' | 'warning';
}

const ValidationIssueSection: React.FC<ValidationIssueSectionProps> = ({
  title,
  issues,
  emptyText,
  variant,
}) => (
  <section className="space-y-2">
    <Text weight="medium">{title}</Text>
    {issues.length === 0 ? (
      <Text size="tiny" color="secondary">
        {emptyText}
      </Text>
    ) : (
      issues.map((issue, index) => (
        <ValidationIssueCard
          key={`${issue.code}-${index}`}
          issue={issue}
          index={index}
          variant={variant}
        />
      ))
    )}
  </section>
);

/** Props for the validation report panel. */
interface ValidationReportPanelProps {
  /** Validation report to render for the selected revision. */
  report: WebsiteTemplateRegistryValidationReport | null;
  /** Whether the validation report is loading. */
  loading: boolean;
  /** Validation report error, when loading failed. */
  error: unknown;
}

const ValidationReportPanel: React.FC<ValidationReportPanelProps> = ({
  report,
  loading,
  error,
}) => (
  <Panel>
    <PanelHeader className="px-4 py-3 border-b">
      <div>
        <Text weight="medium">Reporte de validacion</Text>
        <Text size="tiny" color="secondary" className="mt-1">
          Errores y advertencias de la revision seleccionada.
        </Text>
      </div>
    </PanelHeader>

    {loading ? <Loading /> : null}
    {!loading && error ? (
      <ErrorState
        title="No pudimos cargar el reporte"
        description="Selecciona otra revision o reintenta en unos segundos."
      />
    ) : null}

    {!loading && !error && !report ? (
      <Text size="tiny" color="secondary">
        Selecciona una revision para ver el reporte de validacion.
      </Text>
    ) : null}

    {report ? <ValidationReportContent report={report} /> : null}
  </Panel>
);

/** Props for validation report content. */
interface ValidationReportContentProps {
  /** Validation report to render. */
  report: WebsiteTemplateRegistryValidationReport;
}

const ValidationReportContent: React.FC<ValidationReportContentProps> = ({
  report,
}) => (
  <div className="space-y-4">
    <div className="rounded-xl bg-slate-50 p-4">
      <Text size="tiny" color="secondary">
        Ultima validacion
      </Text>
      <Text size="tiny" weight="medium">
        {formatDateTime(report.checkedAt)}
      </Text>
    </div>

    <ValidationIssueSection
      title="Errores"
      issues={report.errors}
      emptyText="Sin errores."
      variant="error"
    />
    <ValidationIssueSection
      title="Advertencias"
      issues={report.warnings}
      emptyText="Sin advertencias."
      variant="warning"
    />
  </div>
);

export const WebsiteTemplateRegistryPage: React.FC = () => {
  const { showAlert } = useAlert();
  const { hasPermission: canRead, loading: loadingReadPermission } =
    useHasPermissions(WebsiteTemplateRegistryPermission.Read);
  const { hasPermission: canCreate, loading: loadingCreatePermission } =
    useHasPermissions(WebsiteTemplateRegistryPermission.Create);
  const { hasPermission: canUpdate, loading: loadingUpdatePermission } =
    useHasPermissions(WebsiteTemplateRegistryPermission.Update);
  const { hasPermission: canPublish, loading: loadingPublishPermission } =
    useHasPermissions(WebsiteTemplateRegistryPermission.Publish);
  const registry = useWebsiteTemplateRegistry();
  const { createUploadSession, loading: creatingUploadSession } =
    useCreateWebsiteTemplateRegistryUploadSession();
  const { finalizeUpload, loading: finalizingUpload } =
    useFinalizeWebsiteTemplateRegistryUpload();
  const { updateTemplate, loading: updatingTemplate } =
    useUpdateWebsiteTemplateRegistryEntry();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadVisibility, setUploadVisibility] =
    useState<WebsiteTemplateRegistryVisibility>(
      WebsiteTemplateRegistryVisibility.PRIVATE
    );
  const [selectedRegistryTemplateId, setSelectedRegistryTemplateId] = useState<
    string | undefined
  >(undefined);
  const [selectedRevisionId, setSelectedRevisionId] = useState<
    string | undefined
  >(undefined);
  const [metadataDraft, setMetadataDraft] = useState<TemplateMetadataDraft>({
    name: '',
    description: '',
    author: '',
    visibility: WebsiteTemplateRegistryVisibility.PRIVATE,
  });
  const revisions = useWebsiteTemplateRegistryRevisions(
    selectedRegistryTemplateId
  );
  const validationReport = useWebsiteTemplateRegistryValidationReport(
    selectedRegistryTemplateId,
    selectedRevisionId
  );

  const isLoadingPermissions =
    loadingReadPermission ||
    loadingCreatePermission ||
    loadingUpdatePermission ||
    loadingPublishPermission;
  const canCreateUploads = canCreate;

  useEffect(() => {
    if (
      !selectedRegistryTemplateId &&
      registry.registry.items[0]?.registryTemplateId
    ) {
      setSelectedRegistryTemplateId(
        registry.registry.items[0].registryTemplateId
      );
    }
  }, [registry.registry.items, selectedRegistryTemplateId]);

  useEffect(() => {
    if (!revisions.revisions?.revisions.length) {
      setSelectedRevisionId(undefined);
      return;
    }

    const nextRevisionId =
      revisions.revisions.revisions[0]?.revisionId ?? undefined;
    setSelectedRevisionId((currentValue) =>
      currentValue &&
      revisions.revisions?.revisions.some(
        (revision) => revision.revisionId === currentValue
      )
        ? currentValue
        : nextRevisionId
    );
  }, [revisions.revisions]);

  const selectedRegistryEntry = useMemo(
    () =>
      registry.registry.items.find(
        (item) => item.registryTemplateId === selectedRegistryTemplateId
      ) ?? null,
    [registry.registry.items, selectedRegistryTemplateId]
  );

  useEffect(() => {
    if (!selectedRegistryEntry) {
      setMetadataDraft({
        name: '',
        description: '',
        author: '',
        visibility: WebsiteTemplateRegistryVisibility.PRIVATE,
      });
      return;
    }

    setMetadataDraft({
      name:
        selectedRegistryEntry.editableMetadata?.name ??
        selectedRegistryEntry.templateSummary?.name ??
        selectedRegistryEntry.templateKey,
      description:
        selectedRegistryEntry.editableMetadata?.description ??
        selectedRegistryEntry.templateSummary?.description ??
        '',
      author:
        selectedRegistryEntry.editableMetadata?.author ??
        selectedRegistryEntry.templateSummary?.author ??
        '',
      visibility: selectedRegistryEntry.visibility,
    });
  }, [selectedRegistryEntry]);

  const showRegistryAlert = (
    type: 'success' | 'error' | 'warning',
    title: string,
    subtitle?: string
  ): void => {
    showAlert(buildRegistryAlertMessage(title, subtitle), type);
  };

  const handleUpload = async () => {
    const validationError = validateSelectedPackageFile(selectedFile);
    if (validationError) {
      showRegistryAlert('warning', 'Selecciona un paquete', validationError);
      return;
    }

    if (!selectedFile) {
      return;
    }

    try {
      const file = selectedFile;
      const contentType = getUploadContentType(file);

      // The registry adds the revision to the entry with this key, version and
      // visibility, so updating a public template needs a public upload.
      const uploadSession = await createUploadSession({
        fileName: file.name,
        contentLength: file.size,
        contentType,
        visibility: canPublish
          ? uploadVisibility
          : WebsiteTemplateRegistryVisibility.PRIVATE,
      });

      await uploadPackage(
        file,
        uploadSession.uploadUrl,
        uploadSession.uploadHeaders
      );

      const finalized = await finalizeUpload({
        uploadSessionId: uploadSession.uploadSessionId,
      });

      setSelectedRegistryTemplateId(finalized.registryTemplateId);
      setSelectedRevisionId(finalized.revision.revisionId);
      setSelectedFile(null);
      setUploadVisibility(WebsiteTemplateRegistryVisibility.PRIVATE);

      showRegistryAlert(
        'success',
        'Paquete enviado',
        'La validacion del template ya fue encolada.'
      );
    } catch (error) {
      showRegistryAlert(
        'error',
        'No pudimos subir el paquete',
        getUploadErrorMessage(error)
      );
    }
  };

  const handleSaveMetadata = async () => {
    if (!selectedRegistryEntry) {
      return;
    }

    if (
      !canPublish &&
      selectedRegistryEntry.visibility !==
        WebsiteTemplateRegistryVisibility.PUBLIC &&
      metadataDraft.visibility === WebsiteTemplateRegistryVisibility.PUBLIC
    ) {
      showRegistryAlert(
        'warning',
        'No puedes publicar este template',
        'Necesitas permiso para publicar templates del registry.'
      );
      return;
    }

    const shouldSendVisibility =
      canPublish ||
      metadataDraft.visibility !== WebsiteTemplateRegistryVisibility.PUBLIC;

    try {
      const updated = await updateTemplate({
        registryTemplateId: selectedRegistryEntry.registryTemplateId,
        body: {
          name: metadataDraft.name.trim(),
          description: metadataDraft.description.trim() || null,
          author: metadataDraft.author.trim() || null,
          ...(shouldSendVisibility
            ? { visibility: metadataDraft.visibility }
            : {}),
        },
      });

      setSelectedRegistryTemplateId(updated.registryTemplateId);
      showRegistryAlert(
        'success',
        'Template actualizado',
        'Los datos visibles fueron guardados.'
      );
    } catch (error) {
      showRegistryAlert(
        'error',
        'No pudimos actualizar el template',
        getUploadErrorMessage(error)
      );
    }
  };

  if (isLoadingPermissions) {
    return <Loading />;
  }

  if (!canRead) {
    return (
      <ErrorState
        title="No tienes acceso al registry"
        description="Necesitas permisos del website template registry para gestionar paquetes."
      />
    );
  }

  return (
    <div className="space-y-6">
      <Panel>
        <PanelHeader className="px-4 py-3 border-b">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <PiPackageLight className="mt-0.5 h-5 w-5 text-slate-500" />
              <div>
                <Text weight="medium">Website Template Registry</Text>
                <Text size="tiny" color="secondary" className="mt-1">
                  Sube paquetes .zip validados y revisa el estado de cada
                  revision antes de dejar templates disponibles para seleccion.
                </Text>
              </div>
            </div>
            <a
              href={TEMPLATE_REQUIREMENTS_DOC_URL}
              download="website-template-registry-requirements.md"
              className="inline-flex h-[34px] items-center justify-center gap-2 whitespace-nowrap rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              <PiDownloadSimple className="h-4 w-4" />
              Descargar requisitos
            </a>
          </div>
        </PanelHeader>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
          <UploadPackageSection
            canCreateUploads={canCreateUploads}
            selectedFile={selectedFile}
            creatingUploadSession={creatingUploadSession}
            finalizingUpload={finalizingUpload}
            canPublish={canPublish}
            visibility={uploadVisibility}
            onFileChange={setSelectedFile}
            onVisibilityChange={setUploadVisibility}
            onUpload={() => void handleUpload()}
          />
          <RegistryEntriesSection
            items={registry.registry.items}
            loading={registry.loading}
            error={registry.error}
            selectedRegistryTemplateId={selectedRegistryTemplateId}
            onSelect={setSelectedRegistryTemplateId}
          />
          <TemplateMetadataSection
            selectedRegistryEntry={selectedRegistryEntry}
            draft={metadataDraft}
            canUpdate={canUpdate}
            canPublish={canPublish}
            saving={updatingTemplate}
            onDraftChange={setMetadataDraft}
            onSave={() => void handleSaveMetadata()}
          />
        </div>
      </Panel>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,420px)]">
        <RevisionsPanel
          selectedRegistryEntry={selectedRegistryEntry}
          selectedRegistryTemplateId={selectedRegistryTemplateId}
          revisions={revisions.revisions}
          loading={revisions.loading}
          error={revisions.error}
          selectedRevisionId={selectedRevisionId}
          onSelectRevision={setSelectedRevisionId}
        />
        <ValidationReportPanel
          report={validationReport.report}
          loading={validationReport.loading}
          error={validationReport.error}
        />
      </div>
    </div>
  );
};
